import Bold from "lucide-react/dist/esm/icons/bold";
import Italic from "lucide-react/dist/esm/icons/italic";
import Strikethrough from "lucide-react/dist/esm/icons/strikethrough";
import Code from "lucide-react/dist/esm/icons/code";
import Eraser from "lucide-react/dist/esm/icons/eraser";
import Type from "lucide-react/dist/esm/icons/type";
import Heading1 from "lucide-react/dist/esm/icons/heading-1";
import Heading2 from "lucide-react/dist/esm/icons/heading-2";
import Heading3 from "lucide-react/dist/esm/icons/heading-3";
import Heading4 from "lucide-react/dist/esm/icons/heading-4";
import Heading5 from "lucide-react/dist/esm/icons/heading-5";
import Heading6 from "lucide-react/dist/esm/icons/heading-6";
import List from "lucide-react/dist/esm/icons/list";
import ListOrdered from "lucide-react/dist/esm/icons/list-ordered";
import Code2 from "lucide-react/dist/esm/icons/code-2";
import Quote from "lucide-react/dist/esm/icons/quote";
import Minus from "lucide-react/dist/esm/icons/minus";
import Undo2 from "lucide-react/dist/esm/icons/undo-2";
import Redo2 from "lucide-react/dist/esm/icons/redo-2";
import CornerDownLeft from "lucide-react/dist/esm/icons/corner-down-left";
import TableIcon from "lucide-react/dist/esm/icons/table";
import Rows from "lucide-react/dist/esm/icons/rows-2";
import Columns from "lucide-react/dist/esm/icons/columns-2";
import Trash2 from "lucide-react/dist/esm/icons/trash-2";
import ArrowDown from "lucide-react/dist/esm/icons/arrow-down";
import ArrowUp from "lucide-react/dist/esm/icons/arrow-up";
import ArrowLeft from "lucide-react/dist/esm/icons/arrow-left";
import ArrowRight from "lucide-react/dist/esm/icons/arrow-right";
import { Separator } from "@radix-ui/react-separator";
import { TooltipProvider } from "@quake/shared-ui/components/ui/tooltip";
import { ToolbarButton, useCurrentEditor } from "@quake/shared-ui/components/ui/texteditor";
import { EditorConfig } from "./editor-config";
import { ImageDialog } from "./image-dialog";

export const MenuBar = ({ config }: { config: EditorConfig }) => {
  const { editor } = useCurrentEditor();

  if (!editor) {
    return null;
  }

  const hasTextFormatting = config.bold || config.italic || config.strikethrough || config.inlineCode;
  const hasHeadings = config.paragraph || Object.values(config.headings || {}).some(Boolean);
  const hasLists = config.bulletList || config.orderedList;
  const hasBlocks = config.codeBlock || config.blockquote || config.horizontalRule || config.hardBreak;
  const hasMedia = config.image || config.imageUrl;
  const hasTable = config.table;
  const hasActions = config.clearFormatting;
  const hasHistory = config.undo || config.redo;

  return (
    <TooltipProvider delayDuration={300}>
      <div className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex items-center gap-1 p-2 flex-wrap">
          {/* Text Formatting Group */}
          {hasTextFormatting && (
            <>
              <div className="flex items-center gap-1">
                {config.bold && (
                  <ToolbarButton
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    disabled={!editor.can().chain().focus().toggleBold().run()}
                    isActive={editor.isActive("bold")}
                    tooltip="Bold (Ctrl+B)"
                  >
                    <Bold className="h-4 w-4" />
                  </ToolbarButton>
                )}

                {config.italic && (
                  <ToolbarButton
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    disabled={!editor.can().chain().focus().toggleItalic().run()}
                    isActive={editor.isActive("italic")}
                    tooltip="Italic (Ctrl+I)"
                  >
                    <Italic className="h-4 w-4" />
                  </ToolbarButton>
                )}

                {config.strikethrough && (
                  <ToolbarButton
                    onClick={() => editor.chain().focus().toggleStrike().run()}
                    disabled={!editor.can().chain().focus().toggleStrike().run()}
                    isActive={editor.isActive("strike")}
                    tooltip="Strikethrough"
                  >
                    <Strikethrough className="h-4 w-4" />
                  </ToolbarButton>
                )}

                {config.inlineCode && (
                  <ToolbarButton
                    onClick={() => editor.chain().focus().toggleCode().run()}
                    disabled={!editor.can().chain().focus().toggleCode().run()}
                    isActive={editor.isActive("code")}
                    tooltip="Inline Code"
                  >
                    <Code className="h-4 w-4" />
                  </ToolbarButton>
                )}
              </div>
              {(hasHeadings || hasLists || hasBlocks || hasMedia || hasActions || hasHistory) && (
                <Separator orientation="vertical" className="h-6" />
              )}
            </>
          )}

          {/* Headings Group */}
          {hasHeadings && (
            <>
              <div className="flex items-center gap-1">
                {config.paragraph && (
                  <ToolbarButton
                    onClick={() => editor.chain().focus().setParagraph().run()}
                    isActive={editor.isActive("paragraph")}
                    tooltip="Paragraph"
                  >
                    <Type className="h-4 w-4" />
                  </ToolbarButton>
                )}

                {config.headings?.h1 && (
                  <ToolbarButton
                    onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                    isActive={editor.isActive("heading", { level: 1 })}
                    tooltip="Heading 1"
                  >
                    <Heading1 className="h-4 w-4" />
                  </ToolbarButton>
                )}

                {config.headings?.h2 && (
                  <ToolbarButton
                    onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                    isActive={editor.isActive("heading", { level: 2 })}
                    tooltip="Heading 2"
                  >
                    <Heading2 className="h-4 w-4" />
                  </ToolbarButton>
                )}

                {config.headings?.h3 && (
                  <ToolbarButton
                    onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                    isActive={editor.isActive("heading", { level: 3 })}
                    tooltip="Heading 3"
                  >
                    <Heading3 className="h-4 w-4" />
                  </ToolbarButton>
                )}

                {config.headings?.h4 && (
                  <ToolbarButton
                    onClick={() => editor.chain().focus().toggleHeading({ level: 4 }).run()}
                    isActive={editor.isActive("heading", { level: 4 })}
                    tooltip="Heading 4"
                  >
                    <Heading4 className="h-4 w-4" />
                  </ToolbarButton>
                )}

                {config.headings?.h5 && (
                  <ToolbarButton
                    onClick={() => editor.chain().focus().toggleHeading({ level: 5 }).run()}
                    isActive={editor.isActive("heading", { level: 5 })}
                    tooltip="Heading 5"
                  >
                    <Heading5 className="h-4 w-4" />
                  </ToolbarButton>
                )}

                {config.headings?.h6 && (
                  <ToolbarButton
                    onClick={() => editor.chain().focus().toggleHeading({ level: 6 }).run()}
                    isActive={editor.isActive("heading", { level: 6 })}
                    tooltip="Heading 6"
                  >
                    <Heading6 className="h-4 w-4" />
                  </ToolbarButton>
                )}
              </div>
              {(hasLists || hasBlocks || hasMedia || hasActions || hasHistory) && (
                <Separator orientation="vertical" className="h-6" />
              )}
            </>
          )}

          {/* Lists Group */}
          {hasLists && (
            <>
              <div className="flex items-center gap-1">
                {config.bulletList && (
                  <ToolbarButton
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                    isActive={editor.isActive("bulletList")}
                    tooltip="Bullet List"
                  >
                    <List className="h-4 w-4" />
                  </ToolbarButton>
                )}

                {config.orderedList && (
                  <ToolbarButton
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    isActive={editor.isActive("orderedList")}
                    tooltip="Numbered List"
                  >
                    <ListOrdered className="h-4 w-4" />
                  </ToolbarButton>
                )}
              </div>
              {(hasBlocks || hasMedia || hasActions || hasHistory) && (
                <Separator orientation="vertical" className="h-6" />
              )}
            </>
          )}

          {/* Block Elements Group */}
          {hasBlocks && (
            <>
              <div className="flex items-center gap-1">
                {config.codeBlock && (
                  <ToolbarButton
                    onClick={() => editor.chain().focus().toggleCodeBlock().run()}
                    isActive={editor.isActive("codeBlock")}
                    tooltip="Code Block"
                  >
                    <Code2 className="h-4 w-4" />
                  </ToolbarButton>
                )}

                {config.blockquote && (
                  <ToolbarButton
                    onClick={() => editor.chain().focus().toggleBlockquote().run()}
                    isActive={editor.isActive("blockquote")}
                    tooltip="Quote"
                  >
                    <Quote className="h-4 w-4" />
                  </ToolbarButton>
                )}

                {config.horizontalRule && (
                  <ToolbarButton
                    onClick={() => editor.chain().focus().setHorizontalRule().run()}
                    tooltip="Horizontal Rule"
                  >
                    <Minus className="h-4 w-4" />
                  </ToolbarButton>
                )}

                {config.hardBreak && (
                  <ToolbarButton onClick={() => editor.chain().focus().setHardBreak().run()} tooltip="Line Break">
                    <CornerDownLeft className="h-4 w-4" />
                  </ToolbarButton>
                )}
              </div>
              {(hasMedia || hasActions || hasHistory) && <Separator orientation="vertical" className="h-6" />}
            </>
          )}

          {/* Media Group */}
          {hasMedia && (
            <>
              <div className="flex items-center gap-1">
                {(config.image || config.imageUrl) && <ImageDialog config={config} />}
              </div>
              {(hasTable || hasActions || hasHistory) && <Separator orientation="vertical" className="h-6" />}
            </>
          )}

          {/* Table Group */}
          {hasTable && (
            <>
              <div className="flex items-center gap-1">
                <ToolbarButton
                  onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
                  tooltip="Insert Table"
                >
                  <TableIcon className="h-4 w-4" />
                </ToolbarButton>

                {editor.isActive("table") && (
                  <>
                    <ToolbarButton
                      onClick={() => editor.chain().focus().addColumnBefore().run()}
                      tooltip="Add Column Before"
                    >
                      <div className="relative">
                        <Columns className="h-4 w-4" />
                        <ArrowLeft className="h-2 w-2 absolute -left-1 top-1" />
                      </div>
                    </ToolbarButton>
                    <ToolbarButton
                      onClick={() => editor.chain().focus().addColumnAfter().run()}
                      tooltip="Add Column After"
                    >
                      <div className="relative">
                        <Columns className="h-4 w-4" />
                        <ArrowRight className="h-2 w-2 absolute -right-1 top-1" />
                      </div>
                    </ToolbarButton>
                    <ToolbarButton onClick={() => editor.chain().focus().deleteColumn().run()} tooltip="Delete Column">
                      <div className="relative">
                        <Columns className="h-4 w-4 opacity-50" />
                        <Trash2 className="h-2 w-2 absolute top-1 left-1" />
                      </div>
                    </ToolbarButton>

                    <Separator orientation="vertical" className="h-4 mx-1" />

                    <ToolbarButton onClick={() => editor.chain().focus().addRowBefore().run()} tooltip="Add Row Before">
                      <div className="relative">
                        <Rows className="h-4 w-4" />
                        <ArrowUp className="h-2 w-2 absolute top-0 left-1" />
                      </div>
                    </ToolbarButton>
                    <ToolbarButton onClick={() => editor.chain().focus().addRowAfter().run()} tooltip="Add Row After">
                      <div className="relative">
                        <Rows className="h-4 w-4" />
                        <ArrowDown className="h-2 w-2 absolute -bottom-1 left-1" />
                      </div>
                    </ToolbarButton>
                    <ToolbarButton onClick={() => editor.chain().focus().deleteRow().run()} tooltip="Delete Row">
                      <div className="relative">
                        <Rows className="h-4 w-4 opacity-50" />
                        <Trash2 className="h-2 w-2 absolute top-1 left-1" />
                      </div>
                    </ToolbarButton>

                    <Separator orientation="vertical" className="h-4 mx-1" />

                    <ToolbarButton onClick={() => editor.chain().focus().deleteTable().run()} tooltip="Delete Table">
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </ToolbarButton>
                  </>
                )}
              </div>
              {(hasActions || hasHistory) && <Separator orientation="vertical" className="h-6" />}
            </>
          )}

          {/* Actions Group */}
          {hasActions && (
            <>
              <div className="flex items-center gap-1">
                {config.clearFormatting && (
                  <ToolbarButton
                    onClick={() => editor.chain().focus().unsetAllMarks().run()}
                    tooltip="Clear Formatting"
                  >
                    <Eraser className="h-4 w-4" />
                  </ToolbarButton>
                )}
              </div>
              {hasHistory && <Separator orientation="vertical" className="h-6" />}
            </>
          )}

          {/* History Group */}
          {hasHistory && (
            <div className="flex items-center gap-1">
              {config.undo && (
                <ToolbarButton
                  onClick={() => editor.chain().focus().undo().run()}
                  disabled={!editor.can().chain().focus().undo().run()}
                  tooltip="Undo (Ctrl+Z)"
                >
                  <Undo2 className="h-4 w-4" />
                </ToolbarButton>
              )}

              {config.redo && (
                <ToolbarButton
                  onClick={() => editor.chain().focus().redo().run()}
                  disabled={!editor.can().chain().focus().redo().run()}
                  tooltip="Redo (Ctrl+Y)"
                >
                  <Redo2 className="h-4 w-4" />
                </ToolbarButton>
              )}
            </div>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
};
